import React from "react";
import { Box, Typography, Stack, Card, CardContent } from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import EmptyStateCard from "../../../../components/EmptyStateCard";
import ClientOnboardingFlowDialog from "../../../../components/ClientOnboardingFlowDialog";
import useEvents from "../../../../hooks/useEvents";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

type EmptyClientsProps = {
  onAddClient: () => void;
};

const EmptyClients = ({ onAddClient }: EmptyClientsProps) => {
  const events = useEvents();
  const [flowDialogOpen, setFlowDialogOpen] = React.useState(false);
  
  return (
    <Box data-testid="EmptyClientsView" style={{ margin: 24 }}>
      <EmptyStateCard
        text="Welcome to your coaching journey! You don't have any clients yet. Start by adding your first client and helping them reach their fitness goals."
      />
      <Box
        style={{
          marginTop: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack style={{ maxWidth: 600 }} spacing={3}>
          <Typography style={{ textAlign: "center", marginBottom: 8 }}>
            Once you add clients, they can download the Mighty45 app and connect 
            with their unique connect code. You&apos;ll be able to create training 
            plans, track their progress, and support them on their fitness journey.
          </Typography>
          
          {/* Prominent Onboarding Flow Card */}
          <Card
            onClick={() => {
              setFlowDialogOpen(true);
              events.logButtonClick({
                buttonName: "View Client Connection Flow",
                pageName: "Clients",
                tabName: "Empty",
              });
            }}
            sx={{
              cursor: "pointer",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 3,
              boxShadow: "0 8px 24px rgba(102, 126, 234, 0.4)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0 12px 32px rgba(102, 126, 234, 0.5)",
                background: "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PlayCircleOutlineIcon sx={{ fontSize: 32, color: "white" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 0.5,
                      fontSize: "1.25rem",
                    }}
                  >
                    Learn How to Connect Your First Client
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: "0.95rem",
                    }}
                  >
                    Read a step-by-step guide on getting clients connected and started
                  </Typography>
                </Box>
                <ArrowForwardIcon
                  sx={{
                    fontSize: 28,
                    opacity: 0.9,
                    transition: "transform 0.2s",
                    "&:hover": {
                      transform: "translateX(4px)",
                    },
                  }}
                />
              </Stack>
            </CardContent>
          </Card>

          <StandardButton
            data-testid="EmptyClientsView-Button"
            variant="primary"
            onClick={() => {
              onAddClient();
              events.logButtonClick({
                buttonName: "Add Your First Client",
                pageName: "Clients",
                tabName: "Empty",
              });
            }}
            style={{ fontSize: 18 }}
          >
            Add Your First Client
          </StandardButton>
        </Stack>
      </Box>
      <ClientOnboardingFlowDialog
        open={flowDialogOpen}
        onClose={() => setFlowDialogOpen(false)}
      />
    </Box>
  );
};

export default EmptyClients;